import React, { useState } from 'react';
import { useOnboarding } from '../context/OnboardingContext';
import PageLayout from '../components/PageLayout';
import NavigationButtons from '../components/NavigationButtons';
import './OnboardingCycleDates.css';
import { todayISO, toISODate } from '../utils/formDate';
import { validateCycleDate } from '../utils/signupValidation';

// Les deux champs sont facultatifs, mais ce sont eux qui évitent à une nouvelle
// inscrite de tomber sur une page Cycle vide : sans eux, la prédiction est
// ancrée arbitrairement sur « aujourd'hui - 14 jours » tant qu'aucun relevé
// n'a été saisi (voir cycle_router.get_cycle_stats).
const OnboardingCycleDates = () => {
  const { formData, updateField } = useOnboarding();
  const [errors, setErrors] = useState({});
  const today = todayISO();
  // Au-delà d'un an, le backend refuse la date (schemas/user.py). En fuseau local,
  // comme `today` : `toISOString()` (UTC) décalait la borne d'un jour la nuit.
  const oneYearAgoDate = new Date();
  oneYearAgoDate.setDate(oneYearAgoDate.getDate() - 365);
  const oneYearAgo = toISODate(oneYearAgoDate);

  // `min`/`max` ne bornent que le sélecteur : une date tapée au clavier (Safari
  // iOS notamment) les ignore. Le contrôle se fait donc ici, avant d'avancer.
  const handleNext = () => {
    const next = {
      lastPeriodDate: validateCycleDate(formData.lastPeriodDate),
      lastOvulationDate: validateCycleDate(formData.lastOvulationDate),
    };
    setErrors(next);
    return !next.lastPeriodDate && !next.lastOvulationDate;
  };

  const setDate = (field) => (e) => {
    updateField(field, e.target.value);
    setErrors((err) => ({ ...err, [field]: '' }));
  };

  return (
    <PageLayout step={6}>
      <h2 className="title">Où en êtes-vous dans votre cycle ?</h2>
      <p className="subtitle">
        Ces deux dates nous permettent de vous proposer des prédictions dès
        aujourd'hui, sans attendre un mois de suivi. Vous pourrez les corriger
        à tout moment.
      </p>

      <div className="form-group form-group--left">
        <label className="cycle-dates__label" htmlFor="derniere-regles">
          Premier jour de vos dernières règles
        </label>
        <input
          id="derniere-regles"
          name="derniere-regles"
          type="date"
          className="form-input"
          max={today}
          min={oneYearAgo}
          value={formData.lastPeriodDate}
          aria-invalid={!!errors.lastPeriodDate || undefined}
          onChange={setDate('lastPeriodDate')}
        />
        {errors.lastPeriodDate && <p className="signup-error">{errors.lastPeriodDate}</p>}
        <p className="cycle-dates__help">
          Le jour où le saignement a commencé, même s'il était léger.
        </p>
      </div>

      <div className="form-group form-group--left">
        <label className="cycle-dates__label" htmlFor="derniere-ovulation">
          Dernière ovulation connue <span className="cycle-dates__optional">(si vous la suivez)</span>
        </label>
        <input
          id="derniere-ovulation"
          name="derniere-ovulation"
          type="date"
          className="form-input"
          max={today}
          min={oneYearAgo}
          value={formData.lastOvulationDate}
          aria-invalid={!!errors.lastOvulationDate || undefined}
          onChange={setDate('lastOvulationDate')}
        />
        {errors.lastOvulationDate && <p className="signup-error">{errors.lastOvulationDate}</p>}
        <p className="cycle-dates__help">
          Repérée par un test d'ovulation, votre température au réveil ou vos
          pertes. Laissez vide si vous ne la connaissez pas.
        </p>
      </div>

      <p className="cycle-dates__skip">
        Vous ne savez pas ? Passez cette étape : l'app affinera ses prédictions
        au fil de vos saisies.
      </p>

      <NavigationButtons
        nextPath="/onboarding/recapitulatif"
        prevPath="/onboarding/contraception"
        onNext={handleNext}
      />
    </PageLayout>
  );
};

export default OnboardingCycleDates;
